import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, FileText, Search, Printer, Download,
  CheckCircle, XCircle, RefreshCw, User, Mail, Calendar,
  DollarSign, Hash, BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getToken } from '../../services/authService';

interface SimpleUser {
  id: number;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  created_at: string;
}

interface ReceiptData {
  nombre: string;
  email: string;
  fechaPago: string;
  monto: string;
  txId: string;
  servicio: string;
  fechaAcceso: string;
}

const ADMIN_API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/auth', '/admin')
  : (process.env.NODE_ENV === 'production' ? '/api/admin' : 'http://localhost:8080/api/admin');

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`
  };
}

const EMPTY_FORM: ReceiptData = {
  nombre: '',
  email: '',
  fechaPago: new Date().toISOString().split('T')[0],
  monto: '165',
  txId: '',
  servicio: 'Bootcamp Data Engineer Mayo 2026',
  fechaAcceso: new Date().toISOString().split('T')[0],
};

export default function ServiceReceipts() {
  const [form, setForm] = useState<ReceiptData>({ ...EMPTY_FORM });
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // User search
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<SimpleUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 2) { setUserResults([]); return; }
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ search: query, limit: '8', page: '1' });
      const res = await fetch(`${ADMIN_API}/users.php?${params}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setUserResults(data.users);
        setShowDropdown(true);
      }
    } catch {
      /* silent */
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (userSearch) searchUsers(userSearch); }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, searchUsers]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectUser = (user: SimpleUser) => {
    setForm(prev => ({
      ...prev,
      nombre: user.full_name || `${user.first_name} ${user.last_name}`.trim(),
      email: user.email,
      fechaAcceso: user.created_at?.split('T')[0] || prev.fechaAcceso,
    }));
    setUserSearch('');
    setShowDropdown(false);
    showToast(`Datos de ${user.full_name || user.email} cargados`);
  };

  const handleGenerate = () => {
    if (!form.nombre || !form.email || !form.monto) {
      showToast('Completá nombre, email y monto como mínimo', 'error');
      return;
    }
    setShowPreview(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Comprobante — ${form.nombre}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', -apple-system, sans-serif; color: #1a1a1a; padding: 2.5rem; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2rem; padding-bottom: 1.5rem; border-bottom: 2px solid #e5e7eb; }
          .header-left h1 { font-size: 1.4rem; font-weight: 700; }
          .header-left p { font-size: 0.85rem; color: #666; margin-top: 0.15rem; }
          .doc-type { font-size: 1rem; font-weight: 700; color: #2563eb; text-transform: uppercase; letter-spacing: 0.05em; text-align: right; }
          .doc-number { font-size: 0.8rem; color: #666; margin-top: 0.2rem; text-align: right; }
          .section { margin-bottom: 1.6rem; }
          .section-title { font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #888; margin-bottom: 0.5rem; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem 2rem; }
          .info-item label { font-size: 0.75rem; color: #888; display: block; }
          .info-item p { font-size: 0.9rem; font-weight: 500; }
          table { width: 100%; border-collapse: collapse; }
          th { text-align: left; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #888; padding: 0.5rem 0; border-bottom: 1px solid #e5e7eb; }
          td { padding: 0.7rem 0; font-size: 0.9rem; border-bottom: 1px solid #f3f4f6; }
          .amount { text-align: right; font-weight: 600; }
          .total-row td { font-weight: 700; font-size: 1rem; border-top: 2px solid #e5e7eb; border-bottom: none; padding-top: 0.8rem; }
          .delivery-box { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 0.8rem 1rem; }
          .delivery-box p { font-size: 0.85rem; color: #166534; font-weight: 500; }
          .delivery-box .detail { font-size: 0.78rem; color: #15803d; font-weight: 400; margin-top: 0.2rem; }
          .no-refund { margin-top: 1.8rem; padding-top: 1.2rem; border-top: 2px solid #e5e7eb; }
          .no-refund p { font-size: 0.78rem; color: #666; line-height: 1.5; }
          .no-refund strong { color: #dc2626; }
          .signature { margin-top: 2rem; padding-top: 1.2rem; border-top: 1px solid #e5e7eb; }
          .signature p { font-size: 0.9rem; font-weight: 600; }
          .signature .role { font-size: 0.78rem; color: #666; font-weight: 400; }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const docNumber = form.fechaPago && form.nombre
    ? `DA-${form.fechaPago.replace(/-/g, '')}-${form.nombre.split(' ')[0].toUpperCase().slice(0, 4)}`
    : '';

  const refundDeadline = (() => {
    const base = form.fechaAcceso || form.fechaPago;
    if (!base) return '';
    const d = new Date(base + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + 21);
    return d.toISOString().split('T')[0];
  })();

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium flex items-center gap-2 ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-500 hover:text-gray-700 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-gray-900">Comprobantes de Servicio</h1>
              </div>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                Admin
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Form ── */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              Datos del comprobante
            </h2>

            {/* User search */}
            <div className="mb-4" ref={searchRef}>
              <label className="text-xs font-medium text-gray-500 block mb-1">Buscar alumno en plataforma</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Nombre o email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
                {searchLoading && <RefreshCw className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
              </div>
              {showDropdown && userResults.length > 0 && (
                <div className="absolute z-30 mt-1 w-full max-w-[calc(50%-3rem)] bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {userResults.map(u => (
                    <button
                      key={u.id}
                      onClick={() => selectUser(u)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.first_name?.[0] || u.email[0]).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.full_name || u.email.split('@')[0]}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                    <User className="w-3 h-3" /> Nombre completo
                  </label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={e => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Francis De La Rosa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="alumno@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Fecha de pago
                  </label>
                  <input
                    type="date"
                    value={form.fechaPago}
                    onChange={e => setForm({ ...form, fechaPago: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Monto (USD)
                  </label>
                  <input
                    type="number"
                    value={form.monto}
                    onChange={e => setForm({ ...form, monto: e.target.value })}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                  <Hash className="w-3 h-3" /> ID de transacción PayPal
                </label>
                <input
                  type="text"
                  value={form.txId}
                  onChange={e => setForm({ ...form, txId: e.target.value })}
                  placeholder="7ST139402V857120D"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> Servicio
                </label>
                <input
                  type="text"
                  value={form.servicio}
                  onChange={e => setForm({ ...form, servicio: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Fecha de inicio de acceso
                </label>
                <input
                  type="date"
                  value={form.fechaAcceso}
                  onChange={e => setForm({ ...form, fechaAcceso: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleGenerate}
                className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Generar comprobante
              </button>
              {showPreview && (
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  PDF
                </button>
              )}
            </div>
          </div>

          {/* ── Preview ── */}
          <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity ${showPreview ? 'opacity-100' : 'opacity-40'}`}>
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Vista previa</span>
              {showPreview && (
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  <Download className="w-3.5 h-3.5" />
                  Descargar PDF
                </button>
              )}
            </div>

            <div ref={printRef} className="p-6 text-[13px]">
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid #e5e7eb' }}>
                <div>
                  <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111' }}>Datadventure</h1>
                  <p style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.1rem' }}>Luciano Argolo — Servicios educativos digitales</p>
                  <p style={{ fontSize: '0.78rem', color: '#666' }}>lucianorayos1@gmail.com</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Comprobante de entrega de servicio
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.2rem' }}>{docNumber}</div>
                </div>
              </div>

              {/* Client data */}
              <div style={{ marginBottom: '1.3rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', marginBottom: '0.4rem' }}>
                  Datos del cliente
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 2rem' }}>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>Nombre</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{form.nombre || '—'}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>Email</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{form.email || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Service detail */}
              <div style={{ marginBottom: '1.3rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', marginBottom: '0.4rem' }}>
                  Detalle del servicio
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', padding: '0.4rem 0', borderBottom: '1px solid #e5e7eb' }}>Descripción</th>
                      <th style={{ textAlign: 'right', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', padding: '0.4rem 0', borderBottom: '1px solid #e5e7eb' }}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '0.6rem 0', borderBottom: '1px solid #f3f4f6' }}>{form.servicio}</td>
                      <td style={{ padding: '0.6rem 0', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>${parseFloat(form.monto || '0').toFixed(2)} USD</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, fontSize: '0.95rem', borderTop: '2px solid #e5e7eb', paddingTop: '0.7rem' }}>Total pagado</td>
                      <td style={{ fontWeight: 700, fontSize: '0.95rem', borderTop: '2px solid #e5e7eb', paddingTop: '0.7rem', textAlign: 'right' }}>${parseFloat(form.monto || '0').toFixed(2)} USD</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payment info */}
              <div style={{ marginBottom: '1.3rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', marginBottom: '0.4rem' }}>
                  Información de pago
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 2rem' }}>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>Método</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>PayPal</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>ID de transacción</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500, fontFamily: 'monospace' }}>{form.txId || '—'}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>Fecha de pago</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{form.fechaPago}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>Estado</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#16a34a' }}>Completado</span>
                  </div>
                </div>
              </div>

              {/* Delivery confirmation */}
              <div style={{ marginBottom: '1.3rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', marginBottom: '0.4rem' }}>
                  Confirmación de entrega
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.7rem 1rem' }}>
                  <p style={{ fontSize: '0.82rem', color: '#166534', fontWeight: 500 }}>Servicio entregado y activo</p>
                  <p style={{ fontSize: '0.75rem', color: '#15803d', marginTop: '0.2rem' }}>
                    El cliente recibió acceso completo al servicio digital contratado en la dirección de email indicada a partir del {form.fechaAcceso || form.fechaPago}.
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#15803d', marginTop: '0.15rem' }}>
                    El servicio incluye: acceso a plataforma educativa, material de estudio, ejercicios prácticos, clases grabadas, clases grupales en vivo y soporte por comunidad.
                  </p>
                </div>
              </div>

              {/* Policies */}
              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', marginBottom: '0.5rem' }}>
                  Términos y condiciones del servicio
                </div>

                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#444', marginBottom: '0.2rem' }}>1. Política de reembolso</p>
                <p style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.6 }}>
                  El cliente dispone de un plazo de <strong style={{ color: '#1a1a1a' }}>21 días corridos</strong> a partir de la fecha de inicio de acceso para solicitar un reembolso completo del servicio contratado.
                  {refundDeadline && <> Para este comprobante, la <strong style={{ color: '#1a1a1a' }}>fecha límite de reembolso es el {refundDeadline}</strong>.</>}
                  {' '}Transcurrido dicho plazo, <strong style={{ color: '#dc2626' }}>no se admiten reembolsos</strong>, dado que el cliente ha tenido acceso efectivo al contenido y los recursos del programa.
                </p>

                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#444', marginTop: '0.8rem', marginBottom: '0.2rem' }}>2. Política de pagos y acceso continuo</p>
                <p style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.6 }}>
                  En el caso de planes con cuotas mensuales, el pago de cada cuota deberá realizarse entre el día 1 y el día 7 del mes correspondiente.
                  Si el pago no se registra dentro de los <strong style={{ color: '#1a1a1a' }}>10 días posteriores al vencimiento</strong> (es decir, hasta el día 17 del mes en curso),
                  el acceso a todos los contenidos de la plataforma será <strong style={{ color: '#1a1a1a' }}>suspendido temporalmente</strong> hasta que se regularice el pago pendiente.
                  Una vez acreditado el monto adeudado, el acceso se restablecerá de forma inmediata.
                </p>

                <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.8rem', fontStyle: 'italic' }}>
                  Al realizar el pago, el cliente declara haber leído y aceptado estos términos.
                </p>

                <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.6rem' }}>
                  Fecha de emisión del comprobante: {new Date().toISOString().split('T')[0]}
                </p>
              </div>

              {/* Signature */}
              <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '0.85rem', fontWeight: 600 }}>Luciano Argolo</p>
                <p style={{ fontSize: '0.75rem', color: '#666' }}>Datadventure — Instructor y responsable del servicio</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
